import { yupResolver } from '@hookform/resolvers/yup';
import type { FormEventHandler } from 'react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import type { InferType } from 'yup';

import { fieldToDisplayableName } from 'utils/formatters/fieldToDisplayableName';
import { hasValue } from 'utils/hasValue';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { FormError } from '@atoms/FormError/FormError';

import { CancelButton } from '../ConfirmCancelDialog';
import { NEXT, PAYOUT_REQUEST_STEP_NEXT_BUTTON } from '../constants';
import { SpecificField } from '../fields/SpecificField';
import { setFormErrors } from '../formHelpers';
import type { StepProps } from '../steps';

/*
 * Manual Payout flow component for the second step "Payout Request".
 * Can't be replaced by CommonStep.tsx because of passing product_id to AccountSelector
 **/
export const PayoutRequestStep = ({
  onSubmit,
  onCancel,
  fields,
  defaultValues,
  responseField
}: StepProps) => {
  const validationObject = useMemo(() => {
    if (hasValue(fields)) {
      return Object.keys(fields).reduce<Record<string, yup.BaseSchema>>(
        (acc, fieldName: string) => {
          const field = fields[fieldName];
          const label = fieldToDisplayableName(fieldName);
          let schema = yup.string().label(label);

          if (field.access_type !== 'optional') {
            schema = schema.required();
          } else {
            schema = schema.notRequired();
          }

          acc[fieldName] = schema;
          return acc;
        },
        {}
      );
    }
  }, [fields]);

  const schema = yup.object(validationObject).required();
  interface Inputs extends InferType<typeof schema> {
    base: null;
  }

  const form = useForm<Inputs>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: { ...defaultValues }
  });

  const onSubmitHandle: FormEventHandler<HTMLFormElement> = async (e) => {
    try {
      await form.handleSubmit(async (data) => {
        await onSubmit(data);
      })(e);
    } catch (error: any) {
      setFormErrors({
        errors: error.error[responseField],
        setError: form.setError,
        fields: Object.keys(schema.fields)
      });
    }
  };

  if (!fields) return null;

  return (
    <div className="flex justify-center pb-8">
      <form autoComplete="off" className="w-[90%]" onSubmit={onSubmitHandle}>
        {Object.keys(fields).map((fieldName) => {
          return (
            <SpecificField
              data-testid={`payout_request_step_${fieldName}`}
              key={fieldName}
              fieldName={fieldName}
              fieldDefinition={fields[fieldName]}
              form={form}
            />
          );
        })}
        <div className="flex flex-row gap-4">
          <CancelButton onAction={onCancel} />
          <PrimaryButton
            data-testid={PAYOUT_REQUEST_STEP_NEXT_BUTTON}
            htmlType="submit"
            size="large"
            className="w-full"
          >
            {NEXT}
          </PrimaryButton>
        </div>
        <FormError message={form.formState.errors.base?.message} />
      </form>
    </div>
  );
};
